import React, {
  ComponentProps,
  CSSProperties,
  FC,
  useContext,
  useEffect,
} from 'react';
import Alert from '@narvar/nth-block-alert';
import { useTheme, ThemeProvider } from '@narvar/nth-hook-theme';
import { RetailerTheme } from '@narvar/nth-hook-loma';
import InitialPanel from '../InitialPanel';
import VerifyAddressPanel from '../VerifyAddressPanel';
import DeliveryInstructionsPanel from '../DeliveryInstructionsPanel';
import CustomComponentContext from '../CustomComponentContext';
import {
  useDeliveryInstructionsContext,
  UI_STATE,
} from '../../hooks/useDeliveryInstructionsState';
import useGetEligibility from '../../hooks/useGetEligibility';
import { ResponseDeliveryInstructionsEligibilityType } from '../../../server/api/schema';
import SubmitDeliveryInstructionsSuccessPanel from '../SubmitDeliveryInstructionsSuccessPanel';
import DeliveryInstructionsSublimeSuccess from '../DeliveryInstructionsSublimeSuccess';

const DeliveryInstructions: FC = () => {
  const { state, signalContentLoaded } = useContext(CustomComponentContext);
  const {
    localeLanguage,
    localeCountry,
    carrier,
    trackingNumbers,
    retailerMoniker,
  } = state?.pageContext || {};

  const { state: deliveryInstructionsState, update: updateUIState } =
    useDeliveryInstructionsContext();

  const eligibility = useGetEligibility({
    carrier,
    trackingNumber: trackingNumbers ? trackingNumbers[0] : undefined,
    retailerMoniker,
  });

  useEffect(() => {
    if (deliveryInstructionsState.narvarSubmitted) return;
    if (!eligibility?.narvarSubmitted) return;

    updateUIState({
      deliveryInstructionsUIState: UI_STATE.NARVAR_APPLIED_SUBMIT_SUCCESS,
      narvarSubmitted: true,
    });
  }, [eligibility, deliveryInstructionsState.narvarSubmitted, updateUIState]);

  const eligibleForDeliveryInstructions = Boolean(
    eligibility &&
      eligibility.eligibility &&
      (eligibility.narvarSubmitted ||
        eligibility.eligibility !==
          ResponseDeliveryInstructionsEligibilityType.INELIGIBLE),
  );

  const { featureName } = state?.settings || { featureName: '' };
  const featureNameStr = typeof featureName === 'string' ? featureName : '';

  const { panelcontainerNoneNone: panelcontainerStyle } = useTheme() as {
    [key in keyof RetailerTheme]: CSSProperties;
  };

  const { marginBottom } = panelcontainerStyle || {};

  // TODO: remove locale check when we figure out a good way
  // to do locale-based features across multiple retailers
  if (
    !eligibleForDeliveryInstructions ||
    localeLanguage !== 'en' ||
    localeCountry !== 'US'
  )
    return null;

  const currentUIState = deliveryInstructionsState.deliveryInstructionsUIState;

  let panel = null;
  switch (currentUIState) {
    case UI_STATE.INITIAL:
      panel = <InitialPanel eligibility={eligibility} />;
      break;
    case UI_STATE.ADDRESS_VERIFICATION:
      panel = <VerifyAddressPanel featureName={featureNameStr} />;
      break;
    case UI_STATE.DELIVERY_INSTRUCTIONS:
      panel = <DeliveryInstructionsPanel featureName={featureNameStr} />;
      break;
    case UI_STATE.SUBMIT_SUCCESS:
      panel = <SubmitDeliveryInstructionsSuccessPanel />;
      break;
    case UI_STATE.NARVAR_OPTIN_SUBMIT_SUCCESS:
      panel = (
        <DeliveryInstructionsSublimeSuccess
          hasProfile={deliveryInstructionsState.hasProfile}
        />
      );
      break;
    case UI_STATE.NARVAR_APPLIED_SUBMIT_SUCCESS:
      panel = <DeliveryInstructionsSublimeSuccess autoApplied />;
      break;
    default:
      // NOTE: exhaustive checking
      ((x: never): never => x)(currentUIState);
      return null;
  }

  if (signalContentLoaded) signalContentLoaded();

  return (
    <div
      style={{
        paddingBottom: marginBottom || '0.5rem',
        // TODO: remove when component is in `nth`.
        // hide until useTheme returns an actual value to avoid flash of defaults
        display: panelcontainerStyle ? 'block' : 'none',
      }}
    >
      <Alert
        featureName={typeof featureName === 'string' ? featureName : ''}
        show={deliveryInstructionsState.visible}
        dismissible
        dismissOnClick={(): void =>
          updateUIState({
            deliveryInstructionsUIState: currentUIState,
            visible: false,
          })
        }
      >
        {panel}
      </Alert>
    </div>
  );
};

const ThemedDeliveryInstructions: FC<
  ComponentProps<typeof DeliveryInstructions>
> = (props) => {
  const { state } = useContext(CustomComponentContext);
  const { featureName } = state?.settings || { featureName: '' };
  const featureNameStr = typeof featureName === 'string' ? featureName : '';
  return (
    <ThemeProvider
      featureName={featureNameStr}
      themeTokens={['panelcontainerNoneNone']}
    >
      <DeliveryInstructions
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      />
    </ThemeProvider>
  );
};

export default ThemedDeliveryInstructions;
